import React, { useState } from "react";
import { cdnUrl } from "commons/cdn";
import {
  Component1Icon,
  DimensionsIcon,
  HeartFilledIcon,
  StarFilledIcon,
  LayersIcon,
  QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import { Badge } from "@/components/ui/badge";
import { tagNameBadgeVariant } from "commons/tagName";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Skeleton from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useUpdateDrafts, useUpdateTemplates } from "api/index";
import MyLink from "components/ui/MyLink";
import {
  openOnlinesByPainter,
  openTemplatesByPublisher,
} from "commons/worksLinks";
import { LevelOptions } from "config";
import { MyImage } from "components/MyImage";
import { MyMedia } from "components/MyMedia";

const Dot = ({ className }) => (
  <div
    className={cn(
      "inline-block w-4 h-4 mr-2 bg-secondary rounded-full",
      className,
    )}
  />
);

export const DataTableRowText = ({ row, entityKey }) => {
  const data = row.original;

  const { handleUpdate, isMutating } = useUpdateTemplates(data.id);

  const [text, setText] = useState(data[entityKey] || "");
  const [isOpen, setIsOpen] = useState(false);

  const disabled = !text || isMutating || data[entityKey] === text;
  const onSubmit = () => {
    if (disabled) {
      return null;
    }
    handleUpdate(entityKey, text, false);
    setIsOpen(false);
  };

  return (
    <div className="max-w-[500px] min-w-[130px] flex flex-col gap-2">
      <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <DialogTrigger asChild>
          <div
            onClick={() => setIsOpen(true)}
            className={cn(
              "flex h-8 w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm transition-colors",
              "hover:outline-none hover:ring-1 hover:ring-ring hover:border-input hover:shadow-sm",
            )}
          >
            {data[entityKey]}
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update {entityKey}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {entityKey}
              </Label>
              <Input
                className="col-span-3"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={isMutating || !text || text === data[entityKey]}
              onClick={onSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const DataTableRowTextExt = ({ row, entityKey, type }) => {
  const data = row.original;
  const initData = data?.ext?.[entityKey] || "";
  const { handleUpdate, isMutating } = useUpdateDrafts(data.id);

  const [text, setText] = useState(initData);
  const [isOpen, setIsOpen] = useState(false);

  const disabled = !text || isMutating || initData === text;
  const onSubmit = () => {
    if (disabled) {
      return null;
    }
    handleUpdate(entityKey, text, false);
    setIsOpen(false);
  };

  return (
    <div className="max-w-[500px] min-w-[130px] flex flex-col gap-2">
      <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <DialogTrigger asChild>
          <div
            onClick={() => setIsOpen(true)}
            className={cn(
              "flex h-8 w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm transition-colors",
              "hover:outline-none hover:ring-1 hover:ring-ring hover:border-input hover:shadow-sm",
            )}
          >
            {initData}
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update {entityKey}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {entityKey}
              </Label>
              <Input
                className="col-span-3"
                value={text}
                type={type || "text"}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={isMutating || !text || text === data?.ext[entityKey]}
              onClick={onSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const columnInfo = {
  header: "Props",
  cell: ({ row }) => (
    <div className="flex flex-col space-y-2">
      {row.original.ow && (
        <div className="flex items-center">
          <DimensionsIcon className="w-4 h-4 mr-2" />
          {row.original.ow} x {row.original.oh}
        </div>
      )}
      <div className="flex items-center">
        <LayersIcon className="w-4 h-4 mr-2" />
        {row.original.layerCount}
      </div>
      <div className="flex items-center">
        <Component1Icon className="w-4 h-4 mr-2" />
        {row.original.componentCount}
      </div>
    </div>
  ),
};

export const columnCover = {
  header: "Cover",
  accessorKey: "cover",
  cell: ({ row }) => {
    const { cover, videoCover, videoCoverPreview } = row.original;
    const thumbVideoUrl = videoCoverPreview || videoCover;
    if (!cover && !thumbVideoUrl) {
      return <Skeleton className="w-24 h-24 rounded-xl" />;
    }
    return thumbVideoUrl ? (
      <MyMedia
        alt="Cover"
        className="aspect-square h-24 w-24 rounded-md border border-border bg-black"
        videoUrl={thumbVideoUrl}
        previewVideoUrl={videoCover || videoCoverPreview}
        imageUrl={cover}
      />
    ) : (
      <MyImage
        alt="Cover"
        className="aspect-square rounded-md object-contain w-24 h-24 border border-border bg-black"
        src={cdnUrl(cover)}
      />
    );
  },
};

export const columnPreview = {
  header: "preview",
  accessorKey: "preivew",
  cell: ({ row }) => {
    const { preview, videoPreview, videoPreviewPreview } = row.original;
    const thumbVideoUrl = videoPreviewPreview || videoPreview;
    if (!preview && !thumbVideoUrl) {
      return <Skeleton className="h-24 w-24 rounded-xl" />;
    }
    return thumbVideoUrl ? (
      <MyMedia
        alt="Preview"
        className="aspect-square h-24 w-24 rounded-md border border-border bg-black"
        videoUrl={thumbVideoUrl}
        previewVideoUrl={videoPreview || videoPreviewPreview}
        imageUrl={preview}
      />
    ) : (
      <MyImage
        alt="Preview"
        className="aspect-square rounded-md object-contain w-24 h-24 border border-border bg-black"
        src={cdnUrl(preview)}
      />
    );
  },
};

export const columnName = {
  header: "Name",
  accessorKey: "name",
  cell: ({ row }) => (
    <>
      <div className="fw-bold">{row.original.name}</div>
      <div className="text-black/90 text-xs">{row.original.id}</div>
      <p className="text-ellipsis w-56 text-black/60 text-xs line-clamp-2">
        {row.original.intro}
      </p>
    </>
  ),
};

export const columnPublisher = {
  header: "Publisher",
  accessorKey: "publisher",
  cell: ({ row }) => {
    const publisherId = row.original.painter;
    const name = row.original.publisher;
    if (!publisherId) {
      return <span>{name || "-"}</span>;
    }
    return (
      <button
        type="button"
        className="font-medium underline underline-offset-4"
        title={publisherId}
        onClick={() => openTemplatesByPublisher(publisherId)}
      >
        {name || publisherId}
      </button>
    );
  },
};

export const columnPainter = {
  header: "Painter",
  accessorKey: "painter",
  cell: ({ row }) => {
    const painterId = row.original.author;
    const name = row.original.onlinePublisher;
    if (!painterId) {
      return <span>{name || "-"}</span>;
    }
    return (
      <button
        type="button"
        className="font-medium underline underline-offset-4"
        title={painterId}
        onClick={() => openOnlinesByPainter(painterId)}
      >
        {name || painterId}
      </button>
    );
  },
};

export const columnPainterFrames = {
  header: "Painter",
  accessorKey: "painter",
  cell: ({ row }) => {
    const painterId = row.original.author;
    const name = row.original.publisher;
    if (!painterId) {
      return <span>{name || "-"}</span>;
    }
    return (
      <button
        type="button"
        className="font-medium underline underline-offset-4"
        title={painterId}
        onClick={() => openOnlinesByPainter(painterId)}
      >
        {name || painterId}
      </button>
    );
  },
};

export const cloumnOnlinePublisher = {
  header: "Nick name",
  accessorKey: "onlinePublisher",
};

export const columnLabels = {
  header: "Labels",
  accessorKey: "labels",
  cell: ({ row }) => (
    <div className="flex flex-wrap gap-1">
      {row.original.labels?.map((tag) => (
        <Badge variant="outline" key={tag}>
          <small>{tag}</small>
        </Badge>
      ))}
    </div>
  ),
};

export const columnExtLabels = {
  header: "Labels",
  accessorKey: "labels",
  cell: ({ row }) => (
    <div className="flex flex-wrap gap-1">
      {row.original.ext?.labels?.map((tag) => (
        <Badge variant="outline" key={tag}>
          <small>{tag}</small>
        </Badge>
      ))}
    </div>
  ),
};

export const columnTags = {
  header: "Tags",
  accessorKey: "tags",
  cell: ({ row }) => (
    <>
      <div className="flex flex-wrap gap-1">
        {row.original.tags?.map((tag) => (
          <Badge variant={tagNameBadgeVariant(tag)} key={tag}>
            <small>{tag}</small>
          </Badge>
        ))}
      </div>
    </>
  ),
};

export const columnExtTags = {
  header: "Tags",
  accessorKey: "tags",
  cell: ({ row }) => (
    <>
      <div className="flex flex-wrap gap-1">
        {row.original?.ext?.tags?.map((tag) => (
          <Badge variant={tagNameBadgeVariant(tag)} key={tag}>
            <small>{tag}</small>
          </Badge>
        ))}
      </div>
    </>
  ),
};

export const columnLevel = {
  header: "Level",
  accessorKey: "level",
  cell: ({ row }) => (
    <>
      {row.original?.level !== undefined && (
        <Badge variant="outline">
          <small>
            {
              LevelOptions.find(
                (element) =>
                  Number(element.code) === Number(row.original.level),
              )?.name
            }
          </small>
        </Badge>
      )}
    </>
  ),
};

export const columnExtLevel = {
  header: "Level",
  accessorKey: "level",
  cell: ({ row }) => (
    <>
      {row.original?.ext?.level !== undefined && (
        <Badge variant="outline">
          <small>
            {
              LevelOptions.find(
                (element) =>
                  Number(element.code) === Number(row.original?.ext?.level),
              )?.name
            }
          </small>
        </Badge>
      )}
    </>
  ),
};

export const columnSku = {
  header: "sku",
  accessorKey: "sku",
  cell: ({ row }) => (
    <>
      {row.original?.sku && (
        <Badge variant="outline">
          <small>{row.original?.sku}</small>
        </Badge>
      )}
    </>
  ),
};

export const columnExtSku = {
  header: "sku",
  accessorKey: "sku",
  cell: ({ row }) => (
    <>
      {row.original?.ext?.sku && (
        <Badge variant="outline">
          <small>{row.original?.ext?.sku}</small>
        </Badge>
      )}
    </>
  ),
};

export const columnAvatars = {
  header: "Avatars",
  accessorKey: "avatars",
  cell: ({ row }) => (
    <>
      <div>
        <MyLink to={`/admin/avatars?tid=${row.original.id}`} target="_blank">
          avatars:{row.original.avatarCount}
        </MyLink>
      </div>
      <div>
        recreate:{row.original.reCreateCount}/
        {row.original?.dailyCount?.reCreateCount}
      </div>
      <div className="flex items-center text-destructive">
        <HeartFilledIcon className="w-4 h-4 mr-2" />
        {row.original?.likeCount}
      </div>
      <div className="flex items-center text-amber-500">
        <StarFilledIcon className="w-4 h-4 mr-2" />
        {row.original?.starCount}
      </div>
    </>
  ),
};

const formatScheduledTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// template 状态判断， 没有状态字段
export const columnState = {
  header: "State",
  cell: ({ row }) => {
    return (
      <div className="flex flex-col">
        <div className="flex items-center">
          {row.original.state === 0 && (
            <>
              <Dot className="bg-secondary" /> Draft
            </>
          )}
          {row.original.state === 100 && (
            <>
              <Dot className="bg-yellow-500" /> Scheduled
            </>
          )}
          {row.original.state === 200 && (
            <>
              <Dot className="bg-primary" /> InReview
            </>
          )}
          {row.original.state === 300 && (
            <>
              <Dot className="bg-destructive" /> Reject
            </>
          )}
          {row.original.state === 401 && (
            <>
              <Dot className="bg-primary" /> Online
            </>
          )}
          {row.original.state === 600 && (
            <>
              <Dot className="bg-red-500" /> Offline
            </>
          )}
        </div>
        {row.original.state === 100 && row.original.scheduledOnlineTime && (
          <div className="text-xs text-muted-foreground mt-1">
            {formatScheduledTime(row.original.scheduledOnlineTime)}
          </div>
        )}
      </div>
    );
  },
  // cell: ({ row }) => {
  //   if (row.original.isInDrafts) {
  //     return (
  //       <div className="flex items-center">
  //         <Dot className="bg-secondary" /> Draft
  //       </div>
  //     )
  //   }
  //   if (row.original.errors) {
  //     return (
  //       <div className="flex items-center text-destructive">
  //         <Dot className="bg-destructive" />
  //         Reject
  //       </div>
  //     )
  //   }
  //   return (
  //     <div className="flex items-center">
  //       {row.original.publishTime ? (
  //         row.original.ext?.contentLangs?.length > 0 ? (
  //           <>
  //             <Dot className="bg-primary" /> Published
  //           </>
  //         ) : (
  //           <>
  //             <Dot className="bg-red-500" /> Offline
  //           </>
  //         )
  //       ) : row.original.version ? (
  //         <>
  //           <Dot className="bg-yellow-400" />
  //           In review
  //         </>
  //       ) : (
  //         <>
  //           <Dot className="bg-secondary" /> Draft
  //         </>
  //       )}
  //     </div>
  //   )
  // },
};

/** drafts / draftDlcs / spineDrafts 队列状态：0 待审 / 1 已定时（具体时间看 templates/dlcs/spineTemplates） */
export const columnDraftState = {
  header: "State",
  cell: ({ row }) => {
    const state = row.original.state ?? 0;
    return (
      <div className="flex flex-col">
        <div className="flex items-center">
          {state === 1 ? (
            <>
              <Dot className="bg-yellow-500" /> Scheduled
            </>
          ) : (
            <>
              <Dot className="bg-primary" /> InReview
            </>
          )}
        </div>
        {state === 1 && row.original.scheduledOnlineTime && (
          <div className="text-xs text-muted-foreground mt-1">
            {formatScheduledTime(row.original.scheduledOnlineTime)}
          </div>
        )}
      </div>
    );
  },
};

export const columnSimpleLimit = {
  header: "SimpleLimit",
  accessorKey: "simpleLimit",
  cell: ({ row }) => (
    <DataTableRowTextExt row={row} entityKey={"simpleLimit"} type="number" />
  ),
};

export const columnExtContentLangs = {
  header: "ContentLangs",
  accessorKey: "contentLangs",
  cell: ({ row }) => (
    <>
      {row.original.ext?.contentLangs?.map((item) => (
        <>
          <Badge variant="outline" className={"me-1"}>
            <small>{item}</small>
          </Badge>
        </>
      ))}
    </>
  ),
};

/** draft.ext.productIds；空 = 未上任何产品 */
export const columnExtProductIds = {
  header: "Products",
  accessorKey: "productIds",
  cell: ({ row }) => {
    const ids = row.original.ext?.productIds;
    if (!Array.isArray(ids) || ids.length === 0) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {ids.map((item) => (
          <Badge key={item} variant="outline">
            <small>{item}</small>
          </Badge>
        ))}
      </div>
    );
  },
};

/** frame / spineFrame 根上 productIds；空 = 未上任何产品 */
export const columnProductIds = {
  header: "Products",
  accessorKey: "productIds",
  cell: ({ row }) => {
    const ids = row.original.productIds;
    if (!Array.isArray(ids) || ids.length === 0) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {ids.map((item) => (
          <Badge key={item} variant="outline">
            <small>{item}</small>
          </Badge>
        ))}
      </div>
    );
  },
};

export const ErrorsNote = ({ row }) => {
  const errors = row.original?.errors;
  if (!errors) return;
  const list = JSON.parse(errors);
  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <QuestionMarkCircledIcon color="red" />
        </PopoverTrigger>
        <PopoverContent className="p-4" align="start">
          {list?.map((item, index) => (
            <div
              key={
                item?.id ??
                `${item?.label ?? "unknown"}-${item?.message ?? "unknown"}-${index}`
              }
              className="text-[red]"
            >
              <span className="me-2">{item.label}:</span>
              <span>{item.message}</span>
            </div>
          ))}
        </PopoverContent>
      </Popover>
    </>
  );
};
